import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, viewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgtButtonComponent, NgtChipComponent, NgtInputFieldComponent, TranslatePipe } from '@corporate/ng-tebis-base-components';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgComponentOutlet } from '@angular/common';
import { AIComponentMessage } from '../../03_model/ai-chat-message.model';
import { AssistantMessageComponent } from '../../06_component/assistant/assistant-message/assistant-message.component';
import { AssistantService } from '../../04_service/assistant/assistant.service';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'tps-assistant',
  imports: [
    NgtButtonComponent,
    TranslatePipe,
    MatTabsModule,
    MatIconModule,
    NgtInputFieldComponent,
    MatProgressBarModule,
    NgComponentOutlet,
    AssistantMessageComponent,
    MatMenuModule,
    NgtChipComponent,
],
  templateUrl: './assistant.component.html',
  styleUrl: './assistant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssistantComponent {

  assistantService = inject(AssistantService);
  fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  messages = computed(() => [...this.assistantService.messages()].reverse());
  uploadedFiles = this.assistantService.uploadedFiles;

  isOpen = signal(false);

  newMessage = signal("");
  dragOver = signal(false);

  open() {
    this.isOpen.set(true);
  }

  close() {
    this.isOpen.set(false);
  }

  sendMessage() {
    const message = this.newMessage();
    if (message) {
      this.newMessage.set('');
      this.assistantService.addMessage({
        type: 'standard',
        role: 'user',
        content: message,
        requiresFollowUp: true,
      });
    }
  }

  openBrowseFiles() {
    this.fileInputRef()?.nativeElement.click();
  }

  async onFilesSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files?.length) {
      await this.assistantService.addFilesAsContext(files);
      target.value = '';
      this.open();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
  }

  async onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files?.length) {
      await this.assistantService.addFilesAsContext(files);
      this.open();
    }
  }

  removeUploadedFile(index: number) {
    this.assistantService.removeUploadedFileContext(index);
  }

  getUpdateHandler(message: AIComponentMessage) {
    return (newData: object) => {
      this.assistantService.updateMessage(message, newData);
    };
  }

  handleKeydownEvent(event: KeyboardEvent) {
    if (event.key == 'Enter') {
      if (event.shiftKey || event.ctrlKey || event.altKey) {
        // allow line break with Shift+Enter, Ctrl+Enter or Alt+Enter
        // TODO: convert single line field to multiline
      } else {
        event.preventDefault();
        this.sendMessage();
      }
    }
  }
}
